import {
  Button,
  Card,
  CardContent,
  FormInput,
  FormRoot,
  FormTextarea,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { z } from "zod";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useCreateSalaryAdvanceMutation } from "../api/queries";

const Schema = z.object({
  requestDate: z.string().min(1, "ກະລຸນາເລືອກວັນທີ່ຍື່ນ"),
  amount: z
    .string()
    .min(1, "ກະລຸນາໃສ່ຈຳນວນ")
    .refine((v) => Number(v) > 0, "ຈຳນວນຕ້ອງຫຼາຍກວ່າ 0"),
  reason: z.string(),
});

type FormValues = z.infer<typeof Schema>;

export function SalaryAdvanceNewPage() {
  const navigate = useNavigate({ from: "/app/hr/salary-advance/requests/new" });
  const createMutation = useCreateSalaryAdvanceMutation();

  const methods = RHF.useForm<FormValues>({
    resolver: zodResolver(Schema),
    defaultValues: {
      requestDate: new Date().toISOString().slice(0, 10),
      amount: "",
      reason: "",
    },
  });

  const onSubmit = (values: FormValues) => {
    createMutation.mutate(
      {
        requestDate: values.requestDate,
        amount: Number(values.amount),
        reason: values.reason.trim() || null,
      },
      {
        onSuccess: (result) => {
          toast.success("ຍື່ນຄຳຂໍສຳເລັດ");
          navigate({
            to: "/app/hr/salary-advance/requests/$id",
            params: { id: result.id },
          });
        },
        onError: (err: { message?: string }) => {
          const msg = err?.message ?? "";
          if (msg.includes("ADVANCE_ALREADY_ACTIVE")) {
            toast.error("ທ່ານມີຄຳຂໍທີ່ຢູ່ໃນລະຫວ່າງດຳເນີນການຢູ່ແລ້ວ");
            return;
          }
          toast.error(msg || "ເກີດຂໍ້ຜິດພາດ");
        },
      },
    );
  };

  return (
    <>
      <Header />
      <Main>
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="font-bold text-2xl tracking-tight">
              ຍື່ນຄຳຂໍເບິກເງິນເດືອນລ່ວງໜ້າ
            </h1>
            <p className="mt-1 text-muted-foreground">
              ຕື່ມຂໍ້ມູນ ແລ້ວສົ່ງໃຫ້ HR ພິຈາລະນາ
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate({ to: "/app/hr/salary-advance/requests" })}
          >
            <ArrowLeft className="mr-1 size-4" />
            ກັບຄືນ
          </Button>
        </div>

        <div className="mx-auto w-full max-w-2xl">
          <Card>
            <CardContent className="pt-6">
              <FormRoot<FormValues>
                methods={methods}
                onSubmit={onSubmit}
                className="w-full space-y-6"
              >
                <RHF.Controller
                  name="requestDate"
                  render={({ field }) => (
                    <FormInput
                      label="ວັນທີ່ຍື່ນ"
                      type="date"
                      requiredMark
                      {...field}
                    />
                  )}
                />
                <RHF.Controller
                  name="amount"
                  render={({ field }) => (
                    <FormInput
                      label="ຈຳນວນເງິນ (ກີບ)"
                      type="number"
                      min={0}
                      requiredMark
                      placeholder="ຕົວຢ່າງ: 1000000"
                      {...field}
                    />
                  )}
                />
                <RHF.Controller
                  name="reason"
                  render={({ field }) => (
                    <FormTextarea
                      label="ເຫດຜົນ"
                      placeholder="ອະທິບາຍເຫດຜົນ (ຖ້າມີ)"
                      rows={3}
                      {...field}
                    />
                  )}
                />

                <div className="sticky bottom-0 z-10 mt-8 flex flex-wrap items-center justify-between gap-4 border-t bg-background/95 px-4 py-4 backdrop-blur supports-[backdrop-filter]:bg-background/80">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() =>
                      navigate({ to: "/app/hr/salary-advance/requests" })
                    }
                  >
                    ຍົກເລີກ
                  </Button>
                  <Button
                    type="submit"
                    disabled={createMutation.isPending}
                    className="bg-black text-white hover:bg-black/90"
                  >
                    {createMutation.isPending ? "ກຳລັງສົ່ງ..." : "ຍື່ນຄຳຂໍ"}
                  </Button>
                </div>
              </FormRoot>
            </CardContent>
          </Card>
        </div>
      </Main>
    </>
  );
}
